/**
 * @file useDeletedPages.ts
 * @description «Удалённые страницы» (phase19.16 Р3а; SB128, SB63): список
 * мягко удалённых страниц (`GET pages?deleted=1`) и «Восстановить» —
 * страница возвращается скрытой со своим адресом, а если он занят, сервер
 * отвечает 409 `site_page_restore_slug_taken` — тогда кабинет просит новый
 * адрес и повторяет с ним.
 */

import { useCallback, useEffect, useState } from "react";

import { sitePagePublish } from "../../lib/api-site-page-publish";
import type { SitePageDetail, SitePageListItem } from "../../lib/api-site-pages";
import { pagePublishErrorText, parseSlugTaken } from "../../lib/site-builder/page-publish-errors";

/** Исход «Восстановить»: готово, нужен новый адрес или ошибка словами. */
export type RestoreOutcome =
  | { kind: "done"; page: SitePageDetail }
  | { kind: "slug"; pageId: string; takenSlug: string }
  | { kind: "error"; text: string };

export function useDeletedPages(open: boolean) {
  const [items, setItems] = useState<SitePageListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setError(null);
    try {
      setItems(await sitePagePublish.deleted());
    } catch (err) {
      setError(pagePublishErrorText(err, "Не удалось загрузить удалённые страницы. Повторите."));
      setItems([]);
    }
  }, []);

  useEffect(() => {
    if (open) void reload();
    else setItems(null);
  }, [open, reload]);

  const restore = useCallback(
    async (pageId: string, slug?: string): Promise<RestoreOutcome> => {
      try {
        const page = await sitePagePublish.restore(pageId, slug);
        await reload();
        return { kind: "done", page };
      } catch (err) {
        const taken = parseSlugTaken(err);
        if (taken) return { kind: "slug", pageId, takenSlug: taken.slug || slug || "" };
        return { kind: "error", text: pagePublishErrorText(err, "Не удалось восстановить страницу.") };
      }
    },
    [reload],
  );

  return { items, loading: open && items === null, error, reload, restore };
}
